import { DocumentReference } from "@angular/fire/firestore";
import { ProductType } from "../product/enums/product-type.enum";
import { Timeframe } from "./timeframe";
import * as moment from "moment";
export class Order {
  public city: CityData;
  public items: Item[];
  public summary: OrderSummary;
  public user: UserData;
  public venue: VenueData;
  public id: string;

  constructor(data, id) {
    //Step 1: Build User Data
    const _user: UserData = new UserData(
      data.user.reference,
      data.user.name,
      data.user.email,
      data.user.responses
    );

    //Step 2: Build Venue Data
    const _venue: VenueData = new VenueData(
      data.venue.reference,
      data.venue.name
    );

    //Step 3: Build Order Summary
    const _summary: OrderSummary = new OrderSummary(
      data.summary.serviceFee,
      data.summary.subtotal,
      data.summary.tip,
      data.summary.total,
      data.summary.purchasedAt,
      data.summary.paymentType
    );

    //Step 4: Build City Data
    const _city = new CityData(
      data.city.reference,
      data.city.name,
      data.city.state
    );

    const _itemSummary = new ItemSummary(
      data.items[0].summary.price,
      data.items[0].summary.units,
      data.items[0].summary.redeemedAt
    );
    const _event: EventData = new EventData(
      data.items[0].event.reference,
      data.items[0].event.date,
      new Timeframe(
        data.items[0].event.timeframe.begin,
        data.items[0].event.timeframe.begin
      )
    );

    const _product: ProductData = new ProductData(
      data.items[0].product.reference,
      data.items[0].product.name,
      data.items[0].product.warning,
      data.items[0].product.instructions,
      data.items[0].product.type
    );
    const _item: Item = new Item(_event, _user, _product, _itemSummary);
    this.city = _city;
    this.user = _user;
    this.venue = _venue;
    this.summary = _summary;
    this.items = [_item];
    this.id = id;
  }

  toFirebase() {
    return {
      city: this.city.toFirebase(),
      items: this.items.map(item => item.toFirebase()),
      summary: this.summary.toFirebase(),
      user: this.user.toFirebase(),
      venue: this.venue.toFirebase()
    };
  }

  toCSV() {
    return {
      "Purchase Date": moment(this.summary.purchasedAt.toDate()).format("LL"),
      "Purchase Time": moment(this.summary.purchasedAt.toDate()).format("LT"),
      City: this.city.name,
      Venue: this.venue.name,
      "Product Type": ProductType[this.items[0].product.type],
      "Product Name": this.items[0].product.name,
      "Event Date": moment(this.items[0].event.date.toDate()).format("LL"),
      "Customer Name": this.user.name,
      "Customer Email": this.user.email,
      Units: this.items[0].summary.units,
      Price: this.items[0].summary.price,
      Subtotal: this.items[0].summary.subtotal,
      "Service Fee": this.summary.serviceFee,
      Tip: this.summary.tip,
      Total: this.summary.total,
      "Redeem Date":
        this.items[0].summary.redeemedAt != null
          ? moment(this.items[0].summary.redeemedAt.toDate()).format("LL")
          : "N/A",
      "Redeem Time":
        this.items[0].summary.redeemedAt != null
          ? moment(this.items[0].summary.redeemedAt.toDate()).format("LT")
          : "N/A",
      "Additional Product Ingredients": ""
    };
  }
  productName(): string {
    return this.items[0].product.name;
  }
}

export class Item {
  public event: EventData;
  public user: UserData;
  public product: ProductData;
  public summary: ItemSummary;
  constructor(
    event: EventData,
    user: UserData,
    product: ProductData,
    summary: ItemSummary
  ) {
    this.event = event;
    this.user = user;
    this.product = product;
    this.summary = summary;
  }

  get price(): number {
    return this.summary.price;
  }

  get units(): number {
    return this.summary.units;
  }

  get subtotal(): number {
    return this.summary.subtotal;
  }

  get redeemedAt(): Date {
    return this.summary.redeemedAt.toDate();
  }

  toFirebase() {
    return {
      event: this.event.toFirebase(),
      user: this.user.toFirebase(),
      product: this.product.toFirebase(),
      summary: this.summary.toFirebase()
    };
  }
}

export class ItemSummary {
  public price: number;
  public units: number;
  public subtotal: number;
  public redeemedAt: firebase.firestore.Timestamp;

  constructor(
    price: number,
    units: number,
    redeemedAt: firebase.firestore.Timestamp
  ) {
    this.price = price;
    this.units = units;
    this.subtotal = price * units;
    this.redeemedAt = redeemedAt;
  }

  toFirebase() {
    return {
      price: this.price,
      units: this.units,
      subtotal: this.subtotal,
      redeemedAt: this.redeemedAt
    };
  }
}

export abstract class FirebaseData {
  public reference: DocumentReference;
  public path: string;
  public id: string;

  constructor(ref: DocumentReference) {
    this.reference = ref;
    this.id = ref.id;
    this.path = ref.path;
  }
}

export class UserData extends FirebaseData {
  public name: string;
  public email: string;
  public responses: OrderResponse[];

  constructor(
    ref: DocumentReference,
    name: string,
    email: string,
    responses: OrderResponse[]
  ) {
    super(ref);
    this.name = name;
    this.email = email;
    this.responses = responses;
  }

  toFirebase() {
    return {
      reference: this.reference,
      name: this.name,
      email: this.email,
      responses: this.responses.map(qa => qa.toFirebase())
    };
  }
}

export class CityData extends FirebaseData {
  public name: string;
  public state: string;

  constructor(ref: DocumentReference, name: string, state: string) {
    super(ref);
    this.name = name;
    this.state = state;
  }

  toString(): string {
    return this.name + ", " + this.state;
  }
  toFirebase() {
    return {
      reference: this.reference,
      name: this.name,
      state: this.state
    };
  }
}

export class VenueData extends FirebaseData {
  public name: string;

  constructor(ref: DocumentReference, name: string) {
    super(ref);
    this.name = name;
  }

  toFirebase() {
    return {
      reference: this.reference,
      name: this.name
    };
  }
}

export class EventData extends FirebaseData {
  public date: firebase.firestore.Timestamp;
  public timeframe: Timeframe;

  constructor(
    ref: DocumentReference,
    date: firebase.firestore.Timestamp,
    tf: Timeframe
  ) {
    super(ref);
    this.date = date;
    this.timeframe = tf;
  }

  toFirebase() {
    return {
      reference: this.reference,
      date: this.date,
      timeframe: this.timeframe.toJSON()
    };
  }
}

export class ProductData extends FirebaseData {
  public name: string;
  public warning: string;
  public instructions: string;
  public type: ProductType;

  constructor(
    ref: DocumentReference,
    name: string,
    warning: string,
    instructions: string,
    type: ProductType
  ) {
    super(ref);
    this.name = name;
    this.warning = warning;
    this.instructions = instructions;
    this.type = type == ProductType.Legacy ? ProductType.Other : type;
  }

  toFirebase() {
    return {
      reference: this.reference,
      name: this.name,
      warning: this.warning,
      instructions: this.instructions,
      type: this.type
    };
  }
}

export class OrderSummary {
  public serviceFee: number;
  public subtotal: number;
  public tip: number;
  public total: number;
  public purchasedAt: firebase.firestore.Timestamp;
  public paymentType: string;

  constructor(
    sFee: number,
    sTotal: number,
    tip: number,
    total: number,
    p: firebase.firestore.Timestamp,
    type: string
  ) {
    this.serviceFee = sFee;
    this.subtotal = sTotal;
    this.tip = tip;
    this.total = total;
    this.purchasedAt = p;
    this.paymentType = type;
  }

  toFirebase() {
    return {
      serviceFee: this.serviceFee,
      tip: this.tip,
      total: this.total,
      purchasedAt: this.purchasedAt,
      paymentType: this.paymentType
    };
  }
}

export class OrderResponse {
  public question: string;
  public response: string;

  constructor(question: string, response: string) {
    this.question = question;
    this.response = response;
  }

  toFirebase() {
    return {
      question: this.question,
      response: this.response
    };
  }
}
